import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import SydModal from '../../commonDesign/SydModal'
import SydButton from '../../commonDesign/Button'
import SydModalActions from '../../commonDesign/SydModal/SydModalActions'
import DragAndDropListItem from '../GroupingProvider/DragAndDropListItem'
import Switch from '../../molecules/Switch'

const useStyles = makeStyles(() => ({
  list: {
    maxHeight: '60vh',
    overflowY: 'auto',
    padding: '0 0 20px 0'
  },
  columnOption: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    '&.__disabled': {
      color: 'color-mix(in srgb, var(--text-color-main) 50%, transparent)'
    }
  }
}))

function partitionColumns (cols) {
  const visible = []
  const excluded = []
  for (const col of cols) {
    if (col.exclude) excluded.push(col)
    else visible.push(col)
  }
  return { visible, excluded }
}

function GridColumnConfigModal ({ open, onClose, columns, defaultColumns, onSave }) {
  const classes = useStyles()
  const [localColumns, setLocalColumns] = useState([])
  const [excludedColumns, setExcludedColumns] = useState([])

  useEffect(() => {
    if (open && columns) {
      const { visible, excluded } = partitionColumns(columns)
      setLocalColumns(visible.map((col, i) => ({ ...col, _ordinal: i })))
      setExcludedColumns(excluded)
    }
  }, [open, columns])

  const onMoveItem = useCallback((dragIndex, hoverIndex) => {
    setLocalColumns(prev => {
      const next = [...prev]
      const dragged = next[dragIndex]
      next.splice(dragIndex, 1)
      next.splice(hoverIndex, 0, dragged)
      return next.map((col, i) => ({ ...col, _ordinal: i }))
    })
  }, [])

  const toggleItem = useCallback((index) => () => {
    setLocalColumns(prev =>
      prev.map((col, i) => i === index ? { ...col, hidden: !col.hidden } : col)
    )
  }, [])

  const handleSave = useCallback(() => {
    const cleaned = localColumns.map(({ _ordinal, ...col }) => col)
    onSave([...cleaned, ...excludedColumns])
    onClose()
  }, [localColumns, excludedColumns, onSave, onClose])

  const handleReset = useCallback(() => {
    const { visible, excluded } = partitionColumns(defaultColumns)
    setLocalColumns(visible.map((col, i) => ({ ...col, _ordinal: i })))
    setExcludedColumns(excluded)
  }, [defaultColumns])

  return (
    <SydModal
      title='Configure Columns'
      open={open}
      onClose={onClose}
      size='small'
    >
      <div className={classes.list}>
        <DndProvider backend={HTML5Backend}>
          {localColumns.map((col, index) => (
            <DragAndDropListItem
              id={col.name}
              key={col.name}
              index={index}
              onMoveItem={onMoveItem}
            >
              <div className={clsx(classes.columnOption, { __disabled: col.hidden })}>
                <span>{col.displayName || col.name}</span>
                <Switch
                  checked={!col.hidden}
                  onChange={toggleItem(index)}
                />
              </div>
            </DragAndDropListItem>
          ))}
        </DndProvider>
      </div>
      <SydModalActions>
        <SydButton variant='ghost' size='md' onClick={handleReset}>
          Reset to Defaults
        </SydButton>
        <SydButton variant='primary' size='md' onClick={handleSave}>
          Apply
        </SydButton>
      </SydModalActions>
    </SydModal>
  )
}

GridColumnConfigModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  defaultColumns: PropTypes.array.isRequired,
  onSave: PropTypes.func.isRequired
}

export default GridColumnConfigModal
